import { useState, useEffect, ChangeEventHandler, FC } from 'react';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MAX_PAGE_LIMIT } from '../../constants/numbers';
import { IUser } from '../../interfaces/user';
import IResponse, { IPagination } from '../../interfaces/response';
import BasicModal from './BasicModal';
import { LoadingButton } from '@mui/lab';
import AdminDataGrid from './AdminDataGrid';
import IosSwitch from './IosSwitch';
import { DeleteOutline, Edit, Visibility } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';

interface userTableProps {
	userId?: string | undefined;
	tabItem: any;
	activeItem?: string;
	refetch?: boolean;
}

const UserTable: FC<userTableProps> = ({ userId = undefined, tabItem, activeItem = '' }) => {
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [fetchedData, setFetchedData] = useState<IUser[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [loading, setLoading] = useState(true);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const navigate = useNavigate();
	const [deleteUser, setDeleteUser] = useState<IUser>();
	const [refresh, setRefresh] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [data, setData] = useState(fetchedData);
	const [openModal, setOpenModal] = useState<boolean>(false);
	const [checked, setChecked] = useState<boolean>(false);
	const [companyName, setCompanyName] = useState<string>('');
	const [managerName, setManagerName] = useState<string>('');
	const handleChangeSearch: ChangeEventHandler<HTMLInputElement> = e => {
		setSearchTerm(e.target.value);
	};
	const headerClassNames = 'text-xs bg-background-paper';

	useEffect(() => {
		if (searchTerm.length === 0) {
			setData(fetchedData);
		} else {
			// Otherwise, filter the data
			const filterData = fetchedData.filter(item => {
				if (item.title) {
					return item.title.includes(searchTerm);
				} else {
					return item.name.includes(searchTerm);
				}
			});
			setData(filterData);
		}
	}, [searchTerm]);

	const handleFetchData = () => {
		axios<IResponse<IUser[]>>({
			url: tabItem.api && tabItem.api,
			params: {
				limit: pageSize,
				user_id: userId && userId,
				search: '',
				page: page,
				role_id: tabItem.role_id,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(res.data.data);
					setData(res.data.data);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const { t } = useTranslation();
	const handleCreateNewUser = () => {
		if (activeItem === t('manage_companys')) {
			setOpenModal(!openModal);
		} else if (activeItem === t('marketers')) {
			navigate('/admin/marketers/new');
		} else if (activeItem === t('campaigns')) {
			navigate('/campaigns/create');
		} else {
			navigate('/admin/users/new');
		}
	};

	const handleCreate = () => {
		axios<IResponse<boolean>>({
			url: tabItem.api && tabItem.api,
			method: 'post',
			data: {
				title: companyName,
				manager_name: managerName,
				is_active: checked ? 1 : 0,
				user_id: userId,
			},
		}).then(res => {
			if (res.data.success) {
				setOpenModal(false);
				handleFetchData();
			}
		});
	};
	const handleDelete = (id: number) => () => {
		axios<IResponse<boolean>>({
			url: tabItem.postApi && `${tabItem.postApi}/${id}`,
			method: 'DELETE',
		}).then(res => {
			if (res.data.success) {
				handleFetchData();
			}
		});
	};

	const columns: GridColDef<any>[] = [
		{
			field: 'action',
			headerName: t('action'),
			flex: 1,
			headerClassName: headerClassNames,
			headerAlign: 'center',
			align: 'center',
			renderCell: (params: any) => {
				let data = params.row;
				return (
					<>
						{tabItem.view && (
							<IconButton onClick={() => tabItem.viewAction(data.id)}>
								<Visibility className="text-text-secondary" />
							</IconButton>
						)}
						{tabItem.edit && (
							<IconButton onClick={() => tabItem.editAction(data.id, data.status)}>
								<Edit className="text-text-secondary" />
							</IconButton>
						)}
						{tabItem.delete && (
							<IconButton onClick={handleDelete(data.id)}>
								<DeleteOutline className="text-text-secondary" />
							</IconButton>
						)}
					</>
				);
			},
		},
	];

	let CombinationColumns = [...tabItem.columns, ...columns];

	useEffect(() => {
		handleFetchData();
	}, [page, pageSize, refresh, activeItem]);

	const handleCloseDeleteDialog = () => {
		setDeleteUser(undefined);
	};

	const handleDeleteUser = () => {
		setDeleteLoading(true);
		axios<IResponse<any>>({
			url: tabItem.api && `${tabItem.api}/${deleteUser?.id}`,
			method: 'DELETE',
		})
			.then(res => {
				if (res.data.success) {
					setDeleteUser(undefined);
					setRefresh(prev => prev + 1);
				}
			})
			.finally(() => {
				setDeleteLoading(false);
			});
	};
	return (
		<div className={'w-full'}>
			<AdminDataGrid<IUser> loading={loading} activeItem={activeItem} pagination={pagination} rows={data} columns={CombinationColumns} pageSize={pageSize} setPageSize={setPageSize} searchTerm={searchTerm} handleSearch={handleChangeSearch} page={page} setPage={setPage} onClick={handleCreateNewUser} actionButtonLable={t('add_new_user')} />
			<BasicModal
				open={!!openModal}
				onClose={handleCreateNewUser}
				title={t('create_new_company')}
				titleClassName={'text-center grow '}
				subClassName={'flex flex-row-reverse'}
				className={'max-w-xl rounded-lg'}
				showBackIcon={false}
				action={
					<div className="w-full">
						<LoadingButton loading={deleteLoading} onClick={handleCreate} variant="contained" color="primary" className="mx-2">
							{t('register')}
						</LoadingButton>
						<Button onClick={handleCreateNewUser} variant="outlined" className="mx-2" color="error">
							{t('cancel')}
						</Button>
					</div>
				}
			>
				<Typography>{t('', { user: deleteUser?.name })}</Typography>
				<Box className="mt-6 flex justify-between gap-x-3">
					<TextField onChange={e => setCompanyName(e.target.value)} label={t('company_name')} className="w-2/3" />
					<TextField onChange={e => setManagerName(e.target.value)} label={t('manager_name')} className="w-2/3" />
				</Box>
				<Box className="mb-3 mt-2">
					<IosSwitch checked={checked} setValue={setChecked} color="primary" label={t('status_action')} />
				</Box>
			</BasicModal>
			<BasicModal
				open={!!deleteUser}
				onClose={handleCloseDeleteDialog}
				title={t('delete_role')}
				titleClassName={'text-center grow'}
				subClassName={'flex flex-row-reverse'}
				className={'max-w-sm rounded-lg'}
				showBackIcon={false}
				action={
					<>
						<Button onClick={handleCloseDeleteDialog} color="inherit">
							{t('cancel')}
						</Button>
						<LoadingButton loading={deleteLoading} onClick={handleDeleteUser} variant="outlined" color="error">
							{t('delete')}
						</LoadingButton>
					</>
				}
			>
				<Typography>{t('delete_user_description', { user: deleteUser?.name })}</Typography>
			</BasicModal>
		</div>
	);
};

export default UserTable;
